import React from 'react';
import Icon from '../../../components/AppIcon';

const STATUS_CONFIG = {
  pending: { label: 'Pending', color: 'bg-yellow-100 text-yellow-700', icon: 'Clock', desc: 'Your redemption is waiting for teacher approval.' },
  approved: { label: 'Approved', color: 'bg-blue-100 text-blue-700', icon: 'CheckCircle', desc: 'Your redemption has been approved by your teacher.' },
  completed: { label: 'Completed', color: 'bg-green-100 text-green-700', icon: 'CheckCircle2', desc: 'You have received this reward. Enjoy!' },
  rejected: { label: 'Rejected', color: 'bg-red-100 text-red-700', icon: 'XCircle', desc: 'This redemption was not approved by your teacher.' },
};

const CATEGORY_LABELS = {
  academic: 'Academic',
  privilege: 'Privilege',
  experience: 'Experience',
  general: 'General',
};

const RedemptionDetailModal = ({ redemption, onClose }) => {
  if (!redemption) return null;

  const status = STATUS_CONFIG?.[redemption?.status] || STATUS_CONFIG?.pending;
  const reward = redemption?.reward || {};

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden">
        <div className="bg-gradient-to-br from-amber-400 to-orange-500 p-6 relative">
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-1.5 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
          >
            <Icon name="X" size={16} className="text-white" />
          </button>
          <div className="flex items-center space-x-4">
            <div className="w-16 h-16 bg-white/20 rounded-2xl flex items-center justify-center">
              <Icon name={reward?.icon || 'Gift'} size={32} className="text-white" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">{reward?.title || 'Reward'}</h2>
              {reward?.category && (
                <span className="inline-block mt-1 px-2 py-0.5 bg-white/20 text-white text-xs rounded-full">
                  {CATEGORY_LABELS?.[reward?.category] || reward?.category}
                </span>
              )}
            </div>
          </div>
        </div>

        <div className="p-6 space-y-5">
          <div className="flex items-start space-x-3 p-4 rounded-xl bg-gray-50">
            <span className={`flex items-center space-x-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${status?.color}`}>
              <Icon name={status?.icon} size={14} />
              <span>{status?.label}</span>
            </span>
            <p className="text-sm text-muted-foreground pt-1">{status?.desc}</p>
          </div>

          {reward?.description && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Description</p>
              <p className="text-sm text-foreground">{reward?.description}</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div className="bg-amber-50 rounded-xl p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">Stars Spent</p>
              <div className="flex items-center justify-center space-x-1">
                <span className="text-lg">⭐</span>
                <span className="text-2xl font-bold text-amber-600">{redemption?.starsSpent ?? reward?.starCost ?? 0}</span>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 text-center">
              <p className="text-xs text-muted-foreground mb-1">Redeemed On</p>
              <p className="text-sm font-semibold text-foreground">
                {redemption?.redeemedAt
                  ? new Date(redemption?.redeemedAt)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
                  : '—'}
              </p>
            </div>
          </div>

          {redemption?.notes && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">Teacher Notes</p>
              <p className="text-sm text-foreground bg-blue-50 rounded-xl p-3">{redemption?.notes}</p>
            </div>
          )}
        </div>

        <div className="px-6 pb-6">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-xl transition-colors text-sm"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default RedemptionDetailModal;
